import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'

import { orderKeys } from './use-sales-order-query'
import { SSE_URL } from '@/lib/config'
import { createResilientEventSource } from '@/lib/resilient-event-source'

export interface StationNewOrderEvent {
    type: 'new_order'
    order_id: string
    order_code: string
    internal_order_code?: string
    customer_name: string
    customer_phone?: string
    amount?: number
    created_by_name?: string
    driver_assignee_name?: string
    created_at?: string
    /** Server-side timestamp of the event itself — NOT when the order was created. */
    occurred_at?: string
    /** Present only when the "new_order" case is enabled in station voice config. */
    speech_template?: string
}

export interface StationNotificationEvent {
    type: string
    speech_template?: string
    /** Needed by the flight board to reorder + highlight the announced order. */
    order_id?: string
    /** Server-side timestamp of the event itself — NOT when the order was created. */
    occurred_at?: string
    payload?: {
        order_code?: string
        internal_order_code?: string
        customer_name?: string
        customer_phone?: string
        amount?: number
        created_by_name?: string
        driver_assignee_name?: string
    }
}

interface DeliveryEventPayload {
    order_id?: string
    occurred_at?: string
    order_code?: string
    internal_order_code?: string
    customer_name?: string
    customer_phone?: string
    amount?: number
    created_by_name?: string
    driver_assignee_name?: string
    speech_template?: string
}

/**
 * SSE hook for the packing station.
 * Listens for `station_new_order` events for the order queue + new-order
 * audio, and `notification` events for the broader audio fan-out
 * (stock_out done, delivered, payment received, ...).
 * Connection lifecycle (reconnect, session refresh, stall watchdog) is owned
 * by the resilient EventSource wrapper — the station must stay audible
 * unattended.
 */
export function useStationSSE(
    onNewOrder?: (event: StationNewOrderEvent) => void,
    onNotification?: (event: StationNotificationEvent) => void,
) {
    const queryClient = useQueryClient()
    const [connected, setConnected] = useState(false)
    const onNewOrderRef = useRef(onNewOrder)
    onNewOrderRef.current = onNewOrder
    const onNotificationRef = useRef(onNotification)
    onNotificationRef.current = onNotification

    useEffect(() => {
        // Lifecycle broadcasts carry a flat order payload. Reshape into the
        // notification event the audio hook reads (template + raw values), and
        // refresh the flight-board list: an order that changed state may have
        // moved out of the confirmed / stock_out filter and must drop off.
        const forwardDeliveryEvent =
            (type: string) => (event: MessageEvent) => {
                void queryClient.invalidateQueries({
                    queryKey: orderKeys.lists(),
                })
                try {
                    const data = JSON.parse(
                        event.data as string,
                    ) as DeliveryEventPayload
                    onNotificationRef.current?.({
                        type,
                        speech_template: data.speech_template,
                        order_id: data.order_id,
                        occurred_at: data.occurred_at,
                        payload: {
                            order_code: data.order_code,
                            internal_order_code: data.internal_order_code,
                            customer_name: data.customer_name,
                            customer_phone: data.customer_phone,
                            amount: data.amount,
                            created_by_name: data.created_by_name,
                            driver_assignee_name: data.driver_assignee_name,
                        },
                    })
                } catch {
                    // ignore parse errors
                }
            }

        const handle = createResilientEventSource({
            url: SSE_URL,
            onConnectedChange: setConnected,
            listeners: {
                station_new_order: (event) => {
                    void queryClient.invalidateQueries({
                        queryKey: orderKeys.lists(),
                    })
                    try {
                        const data = JSON.parse(
                            event.data as string,
                        ) as StationNewOrderEvent
                        onNewOrderRef.current?.(data)
                    } catch {
                        // ignore parse errors
                    }
                },
                // Generic notifications (stock-out done, delivered, payment
                // received, ...) used for the office speaker fan-out. The
                // packing-queue UI ignores these; only the audio hook reacts.
                notification: (event) => {
                    try {
                        const data = JSON.parse(
                            event.data as string,
                        ) as StationNotificationEvent
                        onNotificationRef.current?.(data)
                    } catch {
                        // ignore parse errors
                    }
                },
                order_start_delivery: forwardDeliveryEvent('in_delivery'),
                order_delivery_success: forwardDeliveryEvent('delivered'),
                order_stock_out_done: forwardDeliveryEvent('stock_out'),
                order_delivery_failed: forwardDeliveryEvent('delivery_failed'),
                order_payment_received:
                    forwardDeliveryEvent('payment_received'),
            },
        })

        return () => {
            handle.close()
        }
    }, [queryClient])

    return { connected }
}
